
import { Vue, Options } from "vue-class-component";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "quill-emoji/dist/quill-emoji.css";
import { PusatResolusiV2Controller } from "@/app/ui/controllers/PusatResolusiV2Controller";
import { MentionsData, TemplateChat } from "@/domain/entities/PusatResolusiV2";
import { SendChatPusatResolusiRequest } from "@/data/payload/api/PusatResolusiV2ApiRequest";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ErrorMessageEntities } from "@/domain/entities/MainApp";
import he from "he";
import { ellipsisString } from "@/app/infrastructures/misc/Utils";
import OverlayPanel from "primevue/overlaypanel";
import AddTemplate from "./add-template.vue";
import Editor from "./chatbox-editor.vue";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { ConvertObjectCamelToSnakeCaseV2 } from "@/app/infrastructures/misc/common-library/ConvertObjectCamelToSnakeCase";
import { VideoUtils } from '@/app/ui/components/file-picker/entities';

interface User {
  id: string;
  name: string;
}

@Options({
  props: {
    modelValue: {
      type: String,
      default: ""
    },
    isSendButton: {
      type: Boolean,
      default: true
    }
  },
  emits: ["update:modelValue"],
  components: {
    OverlayPanel,
    AddTemplate,
    Editor
  }
})
export default class ChatBoxMessage extends Vue {
  modelValue!: string;
  isSendButton!: boolean;
  users: MentionsData[] = [];
  quill: Quill | null = null;
  showMentionList = false;
  mentionQuery = "";
  previewFile: any[] = [];
  isLoading = false;
  isLoadingMention = false;
  private scrollAmount = 100;
  public showLeftButton = false;
  public showRightButton = true;
  templateChat: TemplateChat[] = [];
  isLoadingTemplate = false;
  preventWatcher = false;

  get mentionSelected() {
    return this.isSendButton
      ? PusatResolusiV2Controller.mentionSelected
      : PusatResolusiV2Controller.mentionSelectedFinish;
  }

  get uploadedFiles() {
    return this.isSendButton
      ? PusatResolusiV2Controller.uploadedFiles
      : PusatResolusiV2Controller.uploadedFilesFinish;
  }

  mounted() {
    this.resetValue();
    this.getTemplate();
  }

  filterUsers() {
    if (!this.mentionQuery) {
      this.showMentionList = false;
      return;
    }
    if (this.mentionQuery.length > 1) {
      //search user locally
      const searchTerm = this.mentionQuery.replace("@", "").toLowerCase();
      this.users = this.users.filter((user) =>
        user.name.toLowerCase().includes(searchTerm)
      );
      if (this.users.length > 0 && this.isLoadingMention === false) {
        this.showMentionList = false;
      }
      return;
    }
    this.getMentionData();
  }

  selectMention(user: MentionsData) {
    this.showMentionList = false;
    this.mentionSelected.push(user);
    this.mentionQuery = "";
  }

  removeMention(code: string) {
    const index = this.mentionSelected.findIndex(
      (mention) => mention.code === code
    );
    if (index !== -1) {
      this.mentionSelected.splice(index, 1);
    }
  }

  onBlurMention() {
    this.showMentionList = false;
    this.mentionQuery = "";
  }

  removeHtmlChars(str: string) {
    // Remove HTML tags
    const text = str.replaceAll(/<[^>]*>/g, "");
    // Decode HTML entities
    const decodedText = he.decode(text);
    return decodedText;
  }

  get isDisableSend() {
    return (
      this.removeHtmlChars(this.modelValue).length < 1 ||
      this.countChar > 1000 ||
      this.isLoading
    );
  }

  get countChar() {
    const mentionLength = this.mentionSelected.reduce(
      (acc, mention) => acc + mention.code.length,
      0
    );
    return this.removeHtmlChars(this.modelValue).length + mentionLength;
  }

  handleFileUpload(event: any) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (this.uploadedFiles.length >= this.getMaxFiles()) break;

      if (this.isFileTooLarge(file)) {
        this.showFileSizeError(file);
        continue;
      }

      this.processFile(file);
      this.uploadedFiles.push(file);
    }

    input.value = "";
  }

  getMaxFiles(): number {
    return 3;
  }

  getFileSizeLimit(file: File): number {
    if (this.isImage(file)) return 5 * 1024 * 1024; // 5MB
    if (this.isAttachVideo(file)) return 200 * 1024 * 1024; // 200MB
    return Infinity;
  }

  isImage(file: File): boolean {
    return file.type.startsWith("image/");
  }

  isVideo(file: File): boolean {
    return file.type === "video/mp4" || file.type === "video/webm";
  }

  isAttachVideo(file: File): boolean {
    return (
      file.type === "video/mp4" ||
      file.type === "video/webm" ||
      file.type === "video/quicktime"
    );
  }


  isFileTooLarge(file: File): boolean {
    const maxSize = this.getFileSizeLimit(file);
    return file.size > maxSize;
  }

  showFileSizeError(file: File): void {
    const isImage = this.isImage(file);
    const fileTypeName = isImage ? this.$t('pusatResolusi.chatBox.image') as string : "Video";
    const fileSizeLimit = isImage ? "5 MB" : "200 MB";

    MainAppController.showErrorMessage(
      new ErrorMessageEntities({
        image: "error-token-ngen",
        message: `${this.$t('pusatResolusi.chatBox.uploadErrorMessage')} ${fileTypeName.toLowerCase()} ${this.$t('pusatResolusi.chatBox.anda')}anda`,
        title: `Ukuran file ${fileTypeName} melebihi ${fileSizeLimit}`,
        buttonSuccessText: this.$t('pusatResolusi.success.ok'),
        onClose: () => {
          MainAppController.closeErrorMessage();
        }
      })
    );
  }

    async processFile(file: File): Promise<void> {
    if (this.isImage(file)) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64String = e.target.result;
        this.previewFile.push({ ...file, data: base64String, ext: this.fileExt(file.name) });
      };
      reader.readAsDataURL(file);
    } else if (this.isVideo(file)) {
      try {
        const thumbnail = await VideoUtils.generateVideoThumbnail(file);
        this.previewFile.push({ ...file, data: thumbnail, ext: this.fileExt(file.name) });
      } catch (error) {
        this.previewFile.push({ ...file, ext: this.fileExt(file.name) });
      }
    } else {
      this.previewFile.push({ ...file, name: file.name, ext: this.fileExt(file.name) });
    }
}


  removeFile(id: number) {
    const index = this.uploadedFiles.findIndex((file, index) => index === id);
    if (index !== -1) {
      this.previewFile.splice(index, 1);
      this.uploadedFiles.splice(index, 1);
    }
  }
 
  fileExt(val: any) {
    if (!val) return '';
    const parts = val.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  getFileColor(ext?: any): any {
    if (!ext) return "#EDEDED";

    const extension = this.fileExt(ext).toLowerCase();

    const colorMap = new Map<string, string>([
      ["doc", "#155EEF"],
      ["docx", "#155EEF"],
      ["pdf", "#AD0C00"],
      ["xlsx", "#016F3E"],
      ["xls", "#079455"],
      ["csv", "#EDEDED"],
      ["mp4", "#2468F6"],
      ["webm", "#2468F6"],
      ["mov", "#2468F6"]
    ]);

    return colorMap.get(extension) ?? "#EDEDED";
  }

  isFileVideoMov(val: any) {
    return ["mov"].includes(this.fileExt(val));
  }

  get id() {
    return Number(this.$route.params.id);
  }

  insertTemplate(text: string) {
    this.$emit("update:modelValue", text);
  }

  isAddTemplate = false;
  onAddTemplate() {
    this.isAddTemplate = true;
  }

  public scrollRight(isRight = true) {
    const container = this.$refs.cardContainer as HTMLElement;
    if (container) {
      const maxScroll = container.scrollWidth - container.clientWidth;
      const newScrollPosition = isRight
        ? Math.min(container.scrollLeft + this.scrollAmount, maxScroll)
        : Math.max(container.scrollLeft - this.scrollAmount, 0);

      container.scrollTo({
        left: newScrollPosition,
        behavior: "smooth"
      });

      setTimeout(() => {
        this.updateNavigationButtons();
      }, 500);
    }
  }

  public updateNavigationButtons() {
    const container = this.$refs.cardContainer as HTMLElement;
    if (container) {
      this.showLeftButton = container.scrollLeft > 0;
      this.showRightButton =
        container.scrollLeft < container.scrollWidth - container.clientWidth;
    }
  }

  ellipsisText(val: any, length: number) {
    return ellipsisString(val, length);
  }

  toggleTooltip(event: any, partnerCode: any) {
    const refs: any = this.$refs;
    refs[partnerCode].toggle(event);
  }

  async getTemplate() {
    try {
      this.isLoadingTemplate = true;
      const template = await PusatResolusiV2Controller.getTemplate();
      this.templateChat = template;
    } catch (error) {
      console.error("Error fetching template data:", error);
      this.isLoadingTemplate = false;
      this.templateChat = [];
    } finally {
      this.isLoadingTemplate = false;
    }
  }

  async getMentionData() {
    try {
      this.isLoadingMention = true;
      this.showMentionList = true;
      if (this.mentionQuery.startsWith("@") && this.mentionQuery.length < 2) {
        let response = await PusatResolusiV2Controller.getMention(this.id);
        if (this.mentionSelected.length > 0) {
          response = response.filter(
            (user) =>
              !this.mentionSelected.some(
                (mention) => mention.code === user.code && mention.hubId === user.hubId
              )
          );
        }
        this.users = response;
      }
    } catch (error) {
      this.isLoadingMention = false;
      this.showMentionList = false;
      console.error("Error fetching mention data:", error);
    } finally {
      this.isLoadingMention = false;
      // Clean up the interval after the first execution
    }
  }

  showValueMention(data: any) {
    return `${data.hubName ? data.hubName + " -" : ""} ${
      data.type === "agent" ? "Lion parcel" : data.code
    } - ${data.name}`;
  }

  resetValue() {
    this.preventWatcher = true;
    PusatResolusiV2Controller.setChatMessage("");
    PusatResolusiV2Controller.setMentionSelected([]);
    PusatResolusiV2Controller.setUploadedFiles([]);
    this.previewFile = [];
  }

  async sendChat() {
    try {
      this.isLoading = true;
      const send = await PusatResolusiV2Controller.sendChat({
        id: this.id,
        payload: new SendChatPusatResolusiRequest({
          message: this.modelValue,
          hubId: getHubData()?.hubId || "0",
          mentions: this.mentionSelected,
          attachments: this.uploadedFiles.filter((e): e is File => e !== null)
        })
      });
      if (send) {
        this.resetValue();
      }
    } catch (error) {
      this.isLoading = false;
      this.$toast.add({
        severity: "error",
        detail: this.$t('pusatResolusi.success.failedToSendMessage'),
        life: 5000,
        closable: false,
        editable: false
      });
    } finally {
      this.isLoading = false;
    }
  }

  async generateVideoThumbnail(file: File): Promise<string> {
    return await VideoUtils.generateVideoThumbnail(file);
  }
}
